/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useEffect, useContext,
  forwardRef, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import ChevronDown from '@/public/icons/chevron-down.svg';
import { ThemeContext } from '@/contexts/theme-context';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import ClickAway from '@/components/click-away';
import { LocaleContext } from '@/contexts/locale-context';
import TextDiv from '@/components/text-div';
import { toWebSafeFormat } from '@/utilities/strings';
import LoadingArc from '@/components/loading-arc';

/**
 * TextInput contains an input field, header, error message, and tooltip.
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the dropdown field
 * @param {boolean} smallTitle - Small title size
 * @param {Array} items - in the format { title: 'Item 1', value: 'value1' },
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {number} inputHeight - CSS pixel width. Defaults to 48px
 * @param {boolean} borderless - Whether or not dropdown has borders in dropdown-selection element
 * @param {boolean} noDropdownListGap - Whether or not dropdown list has a gap between itself and dropdown-selection, when shown
 * @param {boolean} withFlexTitle - Whether or not title text should use flexbox to expand its height
 * @param {boolean} required - Whether or not this is a required field
 * @param {string} placeholder - Placeholder text
 * @param {string} labelVersion - Changes the label version displayed.
 * @param {boolean} tooltip - Optional - select item hover tooltip
 * @param {boolean} hiddenError - Optional - Whether or not the error container is hidden
 * @param {function} onChange - Optional function called when the input value changes
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {string} error - Optional. Display this error on component load
 * @param {string} defaultValue - Optional. Input field value on component load
 * @param {boolean} disabled
 * @param {boolean} loading
 */
const Dropdown = forwardRef(({
  inputSelectorId,
  title,
  smallTitle,
  items,
  inputWidth,
  inputHeight,
  required,
  placeholder,
  onChange,
  defaultValue,
  borderless,
  noDropdownListGap,
  withFlexTitle,
  labelVersion,
  tooltip,
  primaryColor,
  hiddenError,
  setContent, // Passed in automatically, no need to set
  error, // Passed in automatically, no need to set
  errorTextDirection, // Passed in automatically, no need to set it
  disabled,
  loading,
  shouldRenderEmptyOptions,
  ariaLabel,
  isSearchable,
}, ref) => {
  let defaultItem = { title: placeholder };

  const [isOpen, setIsOpen] = useState(false);
  const { textColor, alternateTextColor, primaryColor: themePrimaryColor } = useContext(ThemeContext);
  const { textDirection } = useContext(LocaleContext);
  const [selected, setSelected] = useState(defaultItem);
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);
  const itemRefs = useMemo(() => items.map(() => React.createRef()), [items]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchBoxRef = useRef();

  useEffect(() => {
    itemRefs[focusedItemIndex]?.current?.focus();
  }, [focusedItemIndex, itemRefs]);

  useEffect(() => {
    if (defaultValue) {
      const foundDefaultItem = items.find((item) => item.title === defaultValue || item.value === defaultValue);

      if (foundDefaultItem) {
        defaultItem = foundDefaultItem;
      }
    }
    if (!defaultItem.title && items.length > 0) {
      defaultItem = { title: items[0].title, value: items[0].value };
    }

    setSelected(defaultItem);
  }, [items, defaultValue]);

  useEffect(() => {
    // do not update placeholder if there's a selected value
    if (selected?.value) return;

    if (placeholder?.length && !defaultValue) {
      setSelected({ title: placeholder });
    } else {
      setSelected(defaultItem);
    }
  }, [placeholder]);

  const onSelectionChange = (newValue) => {
    if (disabled) return;
    setContent(newValue.value);
    onChange(newValue);
    setSelected(newValue);
    setIsOpen(false);
    if (isSearchable) {
      setSearchTerm('');
    }
  };

  const onKeyDown = (event, value, func) => {
    if (event.key === 'Enter' && value) {
      func(value);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      func(true);
      // focus on the first suggestion
      setFocusedItemIndex(0);
      if (isSearchable) {
        setIsOpen(true);
      }
    }

    if (event.key === 'Tab') {
      func(false);
      setFocusedItemIndex(null);
    }
  };

  const onKeyDownItem = (event, item) => {
    if (event.key === 'Enter') {
      onSelectionChange(item);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (focusedItemIndex > 0) {
        setFocusedItemIndex(focusedItemIndex - 1);
      } else {
        // focus on the last suggestion if the user is on the first one
        setFocusedItemIndex(items.length - 1);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (focusedItemIndex < items.length - 1) {
        setFocusedItemIndex(focusedItemIndex + 1);
      } else {
        // focus on the first suggestion if the user is on the last one
        setFocusedItemIndex(0);
      }
    }

    if (event.key === 'Tab') {
      setIsOpen(false);
      setFocusedItemIndex(null);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setFocusedItemIndex(null);
    if (isSearchable) {
      setSearchTerm('');
    }
  };

  const handleDropdownClick = () => {
    if (!disabled && !loading && !isSearchable) {
      if (!isOpen) {
        setIsOpen(true);
        setFocusedItemIndex(0);
      } else {
        setIsOpen(false);
        setFocusedItemIndex(null);
      }
    }
  };

  const onArrowClick = () => {
    if (!disabled && !loading && isSearchable) {
      if (!isOpen) {
        setIsOpen(true);
        setFocusedItemIndex(0);
      } else {
        setIsOpen(false);
        setFocusedItemIndex(null);
      }
    }
  };

  const onArrowKeyDown = (event) => {
    if (isSearchable && event.key === 'Enter') {
      setIsOpen((oldValue) => !oldValue);
    }
  };

  const onSearchBoxFocus = () => {
    if (!isOpen) {
      let selectedIndex = null;
      if (selected?.value) {
        selectedIndex = items.indexOf(selected);
      }
      setIsOpen(true);
      setFocusedItemIndex(selectedIndex);
      setTimeout(() => searchBoxRef.current.focus(), 0);
    }
  };

  const onSearchBoxChange = (event) => {
    setSearchTerm(event.target.value);
    if (!event.target.value) {
      setSelected(defaultItem);
      setContent(null);
      setFocusedItemIndex(null);
    }
  };

  // listener for tabbing out of the searchbox
  const onSearchBoxKeyDown = (event) => {
    if (event.key === 'Tab') {
      setSearchTerm('');
    }
  };

  const getSearchValue = () => {
    if (searchTerm) return searchTerm;
    if (selected?.value) return selected.title;
    return '';
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return items;
    const term = searchTerm.trim().toLowerCase();

    const filtered = items.filter((option) => {
      const optionTitle = option.title;
      if (!optionTitle) return false;
      return optionTitle.toLowerCase().startsWith(term);
    });

    return filtered;
  }, [items, searchTerm]);

  const getLabelVersion = (item) => {
    if (labelVersion === 'title-only') {
      return item.title;
    }
    if (labelVersion === 'value-only') {
      if (!item.value) return item.title;
      return item.value;
    }
    if (labelVersion === 'both') {
      return `${item.value ? item.value : ''} ${item.value ? ' - ' : ''} ${item.title}`;
    }
    return null;
  };

  const getBorderColor = () => {
    if (disabled) return 'var(--color-light-deeper-grey)';
    if (error) return 'var(--color-error-red)';
    return `var(${primaryColor})`;
  };

  if ((!items || items.length === 0) && !shouldRenderEmptyOptions) return null;

  const dropdownOptions = isSearchable ? filteredOptions : items;

  return (
    <div className={`dropdown${isSearchable ? ' searchable' : ''}`}>
      {title && (
        <div className={`${smallTitle ? 'small-title' : `title ${withFlexTitle ? 'flex' : ''}`}`}>
          <TextDiv>
            {`${title}${required === true ? '*' : ''}`}
          </TextDiv>
        </div>
      )}
      <ClickAway shouldWeListen={isOpen} onClickAway={handleClickAway}>
        <div
          aria-label={`${ariaLabel} ${selected.title}`}
          id={inputSelectorId}
          dir={textDirection}
          className={`dropdown-selection${borderless ? ' borderless' : ''}`}
          onClick={handleDropdownClick}
          onKeyDown={(event) => onKeyDown(event, !isOpen, setIsOpen)}
          role="button"
          tabIndex={isSearchable || disabled ? -1 : 0}
        >
          {!isSearchable && (
            <span id={`${inputSelectorId}-selected-value`} className={`dropdown-selection-text ${selected.value ? '' : ' empty'}`}>
              {getLabelVersion(selected)}
              {loading && <LoadingArc inline />}
            </span>
          )}
          {isSearchable && (
            <input
              ref={searchBoxRef}
              type="text"
              autoComplete="disable"
              className="dropdown-search-box"
              placeholder={placeholder}
              onFocus={onSearchBoxFocus}
              onChange={onSearchBoxChange}
              onKeyDown={onSearchBoxKeyDown}
              value={getSearchValue()}
              tabIndex={isSearchable ? 0 : -1}
            />
          )}
          <div
            className={`dropdown-arrow${textDirection === 'rtl' ? ' rtl' : ''}`}
            role="button"
            onClick={onArrowClick}
            onKeyDown={onArrowKeyDown}
            tabIndex={isSearchable ? 0 : -1}
          >
            <div className={`arrow-down ${isOpen ? 'open' : ''}`}><ChevronDown /></div>
          </div>
        </div>
        <div className={`error ${error ? ' visible' : ''} ${errorTextDirection} ${hiddenError ? 'hidden' : ''}`} ref={ref}>
          {error && <TextDiv>{error}</TextDiv>}
        </div>
        <ul
          id={`${inputSelectorId}-dropdown-list`}
          className={`dropdown-list${isOpen ? ' open' : ''}${noDropdownListGap ? ' no-gap' : ''}`}
        >
          {dropdownOptions.map((item, index) => (
            <li
              key={item.value}
            >
              <div
                aria-label={item.title}
                ref={itemRefs[index]}
                role="button"
                title={tooltip ? `${item.title} (${item.value}) ` : ''}
                className={`dropdown-item${defaultValue === item.value ? ' selected' : ''}`}
                id={`dropdown-item-${toWebSafeFormat(item.value)}`}
                dir={textDirection}
                onClick={() => onSelectionChange(item)}
                onKeyDown={(event) => onKeyDownItem(event, item)}
                tabIndex={-1}
              >
                {getLabelVersion(item)}
              </div>
            </li>
          ))}
        </ul>
      </ClickAway>

      <style jsx>
        {`

          .arrow-down {
            transition: transform .24s;
          }

          .arrow-down.open {
            transform: rotate(180deg);
          }

          .dropdown {
            color: ${textColor};
            cursor: ${
          // eslint-disable-next-line no-nested-ternary
          disabled ? 'default' : loading ? 'progress' : 'pointer'
          };
            display: flex;
            flex-direction: column;
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            position: relative;
            width: ${inputWidth}%;
          }

          .title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
            line-height: 1.2;
            margin-bottom: var(--spacing-small);
          }

          .small-title {
            color: var( --color-title-grey);
            font-size: var(--font-size-label);
            margin-bottom: 12px;
          }

          .title.flex {
            display: flex;
            flex-grow: 1;
          }

          .dropdown-selection {
            align-items: center;
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            border: 1px solid ${getBorderColor()};
            color: var(${disabled ? '--color-mid-grey' : '--color-primary-dark'});
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            display: flex;
            height: ${inputHeight}px;
            padding: ${isSearchable ? '0 14px 0 0' : '0 14px'};
            position: relative;
          }

          .dropdown-selection.borderless {
            border: none;
          }

          .searchable .dropdown-selection {
            border: none;
            outline: none;
          }

          .dropdown-selection-text {
            max-width: 90%;
            overflow: hidden;
            padding-right: ${textDirection === 'ltr' ? '14px' : 'initial'};
            padding-left: ${textDirection === 'rtl' ? '14px' : 'initial'};
            text-overflow: ellipsis;
            white-space: nowrap;
            user-select: none;
            display: flex;
            align-items: center;
            gap: var(--spacing);
          }

          .dropdown-selection-text.empty {
            color: var(${primaryColor});
            font-size: var(--font-size-address);
            font-weight: var(--font-weight-body);
          }

          .dropdown-search-box {
            border: 1px solid var(${primaryColor});
            border-width: 1px 0 1px 1px;
            border-radius: var(--border-radius) 0 0 var(--border-radius);
            color: var(--color-primary-dark);
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            height: ${inputHeight}px;
            padding: var(--spacing-small);
            width: 100%;
            z-index: var(--zindex-sticky);
          }

          .dropdown-search-box::placeholder { 
            color: var(--color-mid-grey);
            font-size: var(--font-size-address);
          }

          .dropdown-search-box:disabled { 
            color: var(--color-mid-grey);
            background-color: var(--color-white);
          }

          .dropdown-arrow {
            align-items: center;
            border-radius: var(--border-radius);
            border: 1px solid ${getBorderColor()};
            display: flex;
            height: ${inputHeight}px;
            justify-content: center;
            margin-left: auto;
            margin-right: -15px;
            text-align: center;
            width: 48px;
          }

          .dropdown-selection.borderless .dropdown-arrow {
            border: none;
          }

          .dropdown-arrow.rtl {
            margin-right: auto;
            margin-left: -15px;
          }

          .dropdown-list { 
            animation: fadeIn 0.2s ease-in-out;
            align-items: flex-start;
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            border: 1px solid var(--color-line-grey);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
            display: none;
            flex-direction: column;
            margin-top: ${hiddenError ? 2 : -17}px;
            max-height: 240px;
            overflow-y: auto;
            position: absolute;
            scroll-behavior: smooth;
            scrollbar-width: thin;
            scrollbar-color: var(${primaryColor}) var(--color-white);
            width: 100%;
            z-index: var(--zindex-dropdown);
          }

          .dropdown-list.no-gap {
            margin-top: ${hiddenError ? 0 : -22}px;
            border-radius: 0 0 3px 3px;
          }

          .dropdown-list.open {
            display: block;
            padding: 0;
            list-style: none;
          }

          .dropdown-item {
            align-items: center;
            border-bottom: 1px solid var(--color-line-grey);
            display: flex;
            line-height: 24px;
            min-height: ${inputHeight}px;
            overflow: hidden;
            padding: 12px 30px 12px 15px;
            word-break: break-word;
            width: 100%;
          }

          .dropdown-item.selected {
            background-color: var(--color-line-grey);
          }

          .dropdown-item:hover, .dropdown-item:focus {
            background-color: ${themePrimaryColor || 'var(--color-hover-dark-blue)'};
            color: ${alternateTextColor};
            outline: none;
          }

          .dropdown-item:last-child {
            border-bottom: 'none';
          }

          .error {
            margin-top: 3px;
            min-height: 20px;
            visibility: hidden;
            white-space: break-spaces;
          }

          .error.visible {
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 3px;
            text-align: left;
            visibility: visible;
          }

          .error.visible + .dropdown-list.open {
            margin-top: -18px;
          }

          .error.rtl {
            text-align: right;
          }

          .error.hidden {
            display: none;
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(-30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media only screen and (max-width: 768px) {
            .dropdown {
              font-size: var(--font-size-body-mobile);
              font-weight: var(--font-weight-body-mobile);
              width: 100%;
            }

            .title {
              font-size: var(--font-size-H2-mobile);
              font-weight: var(--font-weight-H2);
            }

            .error.visible {
              font-size: var(--font-size-error-mobile);
            }
          }
        `}
      </style>
    </div>
  );
});

Dropdown.defaultProps = {
  title: null,
  smallTitle: false,
  required: false,
  defaultValue: null,
  onChange: () => {},
  inputSelectorId: null,
  error: null,
  placeholder: '',
  inputWidth: 100,
  inputHeight: 48,
  primaryColor: '--color-mid-grey',
  borderless: false,
  noDropdownListGap: false,
  withFlexTitle: false,
  labelVersion: 'title-only',
  tooltip: false,
  hiddenError: false,
  errorTextDirection: 'ltr',
  disabled: false,
  loading: false,
  shouldRenderEmptyOptions: false,
  isSearchable: false,
  ariaLabel: null,
};

Dropdown.propTypes = {
  title: PropTypes.string,
  smallTitle: PropTypes.bool,
  required: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.string,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  inputSelectorId: PropTypes.string,
  setContent: PropTypes.func.isRequired,
  error: PropTypes.string,
  inputWidth: PropTypes.number,
  inputHeight: PropTypes.number,
  primaryColor: PropTypes.string,
  borderless: PropTypes.bool,
  noDropdownListGap: PropTypes.bool,
  withFlexTitle: PropTypes.bool,
  labelVersion: PropTypes.string,
  tooltip: PropTypes.bool,
  hiddenError: PropTypes.bool,
  errorTextDirection: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  shouldRenderEmptyOptions: PropTypes.bool,
  isSearchable: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default ErrorReportingHOC(Dropdown);
